<script setup>
import { useStore } from 'vuex'
import TitleAlertSaveImport from '@/components/common/TitleAlertSaveImport.vue'
import ImportFileStepper from '@/components/common/ImportFileStepper.vue'
import DownloadDialog from '@/components/common/DownloadDialog.vue'
import CrestLevelBoundaryConditionsTable from '@/components/boundary-condition/CrestLevelBoundaryConditionsTable.vue'
import BoundaryConditionWarning from '@/components/boundary-condition/BoundaryConditionWarning.vue'
import BoundaryConditionAlert from '@/components/boundary-condition/BoundaryConditionAlert.vue'
import { boundaryConditionMixin } from '@/mixins/boundaryConditionMixin'
import { computed, onMounted, ref } from 'vue'

const store = useStore()

const STORE_NAMESPACE = 'crestLevelBoundaryCondition'
const crestLevelBoundaryCondition = ref(null)

const {
  dialog,
  options,
  downloadDialog,
  exportDialog,
  writeLoading,
  fetchLoading,
  count,
  downloadLoading,
  exportLoading,
  items,
  importItems,
  ignoredReferencePoint,
  importItemCount,
  loading,
  templateFileName,
  exportFileName,
  years,
  importedYears,
  addNewItem,
  fetchYears,
  fetchAvailableReferencePoints,
  fetchOptions,
  changePaginatedPage,
  downloadTemplate,
  exportData,
  saveChanges
} = boundaryConditionMixin(STORE_NAMESPACE, crestLevelBoundaryCondition)

const deleteWarning = computed(() => store.state.crestLevelBoundaryCondition.deleteWarning)
const overtoppingRates = computed(() => store.state.crestLevelBoundaryCondition.overtoppingRates)
const importCrestLevelOvertoppingRates = computed(
  () => store.state.crestLevelBoundaryCondition.importOverToppingRates
)

const resetItemState = () => store.dispatch('crestLevelBoundaryCondition/resetItemState')
const fetchCrestLevelOvertoppingRates = async () =>
  store.dispatch('crestLevelBoundaryCondition/fetchCrestLevelOvertoppingRates')
const handleResetItemState = () => {
  if (deleteWarning.value) {
    resetItemState()
  }
}
onMounted(async () => {
  fetchLoading.value = true
  await fetchYears()
  await fetchAvailableReferencePoints()
  if (overtoppingRates.value.length === 0) {
    await fetchCrestLevelOvertoppingRates()
  }
  await fetchOptions()
  fetchLoading.value = false
})
</script>

<template>
  <v-card style="min-height: 8px" :loading="loading" variant="flat" rounded="0">
    <template #loader="{ isActive }">
      <v-progress-linear :active="isActive" indeterminate color="primary" class="mx-1" />
    </template>
    <BoundaryConditionAlert
      v-if="!loading && !count"
      type="crest level"
      @import="dialog = true"
      @download="downloadDialog = true"
    />
    <div v-else>
      <CrestLevelBoundaryConditionsTable
        v-if="options"
        ref="crestLevelBoundaryCondition"
        @changePage="changePaginatedPage"
        :items="items"
        :count="count"
        :years="years"
        :overtoppingRates="overtoppingRates"
      >
        <template v-slot>
          <TitleAlertSaveImport
            export-text="data export"
            import-text="data import"
            download-text="template"
            v-if="!loading"
            @add="addNewItem"
            @save="saveChanges()"
            :loading-save="writeLoading"
            @import="dialog = true"
            :download-button="true"
            @download="downloadDialog = true"
            :export-button="true"
            @export="exportDialog = true"
          />
        </template>
      </CrestLevelBoundaryConditionsTable>
    </div>
    <v-dialog v-model="dialog" max-width="85%">
      <ImportFileStepper
        @close-dialog="dialog = false"
        :storeNamespace="STORE_NAMESPACE"
        text="crest level boundary condition data"
        @resetItemState="handleResetItemState()"
      >
        <template v-slot>
          <BoundaryConditionWarning
            :ignored-reference-point="ignoredReferencePoint"
            :import-warning="deleteWarning"
          />
          <CrestLevelBoundaryConditionsTable
            :items="importItems"
            :overtoppingRates="importCrestLevelOvertoppingRates"
            :count="importItemCount"
            :years="importedYears"
            :editable="false"
          />
        </template>
      </ImportFileStepper>
    </v-dialog>
    <DownloadDialog
      @update:dialog="downloadDialog = $event"
      :dialog="downloadDialog"
      width="500"
      :file-name="templateFileName"
      :loading="downloadLoading"
      text="Download create level template"
      @download="downloadTemplate()"
    ></DownloadDialog>
    <DownloadDialog
      @update:dialog="exportDialog = $event"
      :dialog="exportDialog"
      width="500"
      :file-name="exportFileName"
      :loading="exportLoading"
      text="Export create level data"
      @download="exportData()"
    ></DownloadDialog>
  </v-card>
</template>
